import { Compass, ShieldCheck, Timer } from "lucide-react";
import { CTAButton, Marquee, MaskedLines, ParallaxImage, Reveal, SectionTag } from "../components/ui-bits";
import { IMAGES, MARQUEE_ITEMS } from "../content";

const VALUES = [
    {
        icon: ShieldCheck,
        title: "Precision over promises",
        body: "We don't say 'compliant-ish.' Every sign is measured against §703 and CBC 11B before it leaves the shop — character height, dot profile, LRV contrast, mounting spec.",
    },
    {
        icon: Timer,
        title: "Speed without shortcuts",
        body: "Inspections don't wait, and neither do we. 48-hour quotes, two-to-three-week turnarounds, and after-hours installs that never disrupt your tenants.",
    },
    {
        icon: Compass,
        title: "One vendor, zero finger-pointing",
        body: "Audit, design, fabrication, installation, documentation — one accountable team from the first walkthrough to the inspector's signature.",
    },
];

const COVERAGE = [
    "Downtown LA", "Hollywood", "Santa Monica", "Beverly Hills", "Pasadena", "Long Beach",
    "Burbank", "Glendale", "Culver City", "Torrance", "Orange County", "Ventura County",
];

export default function About() {
    return (
        <div data-testid="about-page">
            <section className="blueprint-grid border-b border-inkline">
                <div className="mx-auto max-w-[1440px] px-6 pb-16 pt-16 md:px-10 lg:pb-24 lg:pt-24">
                    <SectionTag>About us</SectionTag>
                    <MaskedLines
                        lines={["BUILT IN LA,", "FOR LA'S", "BUILDINGS."]}
                        className="font-display text-[13vw] font-bold leading-[0.9] tracking-tighter sm:text-7xl lg:text-[6rem]"
                        accentIndexes={[1]}
                    />
                </div>
            </section>

            <section data-testid="about-story" className="border-b border-inkline">
                <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-12 px-6 py-24 md:px-10 lg:grid-cols-12 lg:gap-6">
                    <div className="lg:col-span-5">
                        <div className="relative">
                            <div className="absolute -left-4 -top-4 h-full w-full bg-volt" aria-hidden="true" />
                            <ParallaxImage
                                src={IMAGES.skyline}
                                alt="Los Angeles skyline at sunset"
                                className="relative aspect-[3/4] w-full border border-inkline"
                                speed={0.1}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col justify-center lg:col-span-7 lg:pl-12">
                        <Reveal>
                            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight md:text-5xl">
                                We started because LA's buildings deserved better than "close enough."
                            </h2>
                            <div className="mt-8 max-w-xl space-y-5 text-base leading-relaxed text-smoke md:text-lg">
                                <p>
                                    Los Angeles ADA Signage Co. was founded by fabricators and project managers who kept
                                    watching the same story play out: a building fails inspection, a demand letter
                                    arrives, and the owner learns their "ADA signs" were never compliant to begin with.
                                </p>
                                <p>
                                    California layers Title 24 on top of the federal ADA — geometric restroom symbols,
                                    70% contrast, tactile exits — and most sign shops only know half that sentence. We
                                    built a company that knows the whole book, fabricates locally in Southern
                                    California, and stands behind every install with documentation an inspector can
                                    verify line by line.
                                </p>
                                <p>
                                    Today we've signed more than 500 buildings across Greater Los Angeles: hospitals,
                                    hotels, offices, schools, and multifamily properties — with a 100% first-pass
                                    inspection record we intend to keep.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section data-testid="about-values" className="border-b border-inkline bg-paper">
                <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
                    <Reveal>
                        <SectionTag>How we work</SectionTag>
                        <h2 className="max-w-2xl font-display text-4xl font-bold leading-none tracking-tight md:text-6xl">
                            Three rules on every job.
                        </h2>
                    </Reveal>
                    <div className="mt-16 grid grid-cols-1 gap-px border border-inkline bg-[rgb(17_17_17/0.12)] lg:grid-cols-3">
                        {VALUES.map((v, i) => (
                            <Reveal key={v.title} delay={i * 0.1} className="group bg-paper p-8 transition-colors duration-500 hover:bg-ink md:p-10">
                                <v.icon className="h-8 w-8 text-bluebrand transition-colors duration-500 group-hover:text-volt" strokeWidth={1.5} />
                                <h3 className="mt-6 font-display text-xl font-bold tracking-tight transition-colors duration-500 group-hover:text-bone md:text-2xl">
                                    {v.title}
                                </h3>
                                <p className="mt-4 text-sm leading-relaxed text-smoke transition-colors duration-500 group-hover:text-bone/70">
                                    {v.body}
                                </p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <Marquee items={MARQUEE_ITEMS} dark />

            <section data-testid="about-coverage" className="border-b border-inkline">
                <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
                    <Reveal>
                        <SectionTag>Coverage area</SectionTag>
                        <h2 className="max-w-2xl font-display text-4xl font-bold leading-none tracking-tight md:text-6xl">
                            From DTLA to the coast.
                        </h2>
                    </Reveal>
                    <div className="mt-14 flex flex-wrap gap-3">
                        {COVERAGE.map((c, i) => (
                            <Reveal key={c} delay={i * 0.04} y={14}>
                                <span data-testid={`coverage-chip-${i}`} className="inline-block cursor-default rounded-full border border-inkline px-5 py-2.5 text-sm font-semibold transition-[background-color,color,border-color] duration-300 hover:border-bluebrand hover:bg-bluebrand hover:text-white">
                                    {c}
                                </span>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.15} className="mt-14">
                        <CTAButton to="/get-started" variant="blue" testid="about-cta-start">
                            Work with us
                        </CTAButton>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
